import { Core } from "@faire/design-tokens";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackPromotionalEventsPdpPromoBannerClick } from "@faire/web-api--source/events/promotionalEvents/click/pdpPromoBanner";
import { trackPromotionalEventsPdpPromoBannerView } from "@faire/web-api--source/events/promotionalEvents/view/pdpPromoBanner";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import { FormattedText } from "@faire/retailer/components/FormattedText";

import { CtaLink } from "./CtaLink";
import { PromoIcon } from "./PromoIcon";

interface IProps {
  promotions?: IInformationMessage[];
  position?: keyof typeof IInformationMessage.Position;
}

const ProductPromoBannersBase: React.FC<IProps> = (props) => {
  const [tooltipIndex, setTooltipIndex] = useState(-1);
  if (!props.promotions?.length) {
    return null;
  }

  const promotionsToShow = props.promotions.filter((productPromotion) => {
    const bannerPosition =
      productPromotion.position ?? IInformationMessage.Position.DEFAULT;

    return (
      productPromotion.type !== IInformationMessage.Type.CATEGORY_RESTRICTION &&
      productPromotion.type !==
        IInformationMessage.Type.GUARANTEED_FREE_SHIPPING_THRESHOLD &&
      bannerPosition === props.position
    );
  });

  if (!promotionsToShow?.length) {
    return null;
  }

  const toggleTooltip = (index: number) =>
    setTooltipIndex((currentIndex) => (currentIndex === index ? -1 : index));

  return (
    <>
      <Spacer size="2x" />
      <Column gap="2x">
        {promotionsToShow.map((productPromotion, index) => (
          <ProductPromoBanner
            key={index}
            productPromotion={productPromotion}
            index={index}
            toggleTooltip={toggleTooltip}
            tooltipIndex={tooltipIndex}
          />
        ))}
      </Column>
    </>
  );
};

export const ProductPromoBanners = observer(ProductPromoBannersBase);

const ProductPromoBanner: React.FC<{
  productPromotion: IInformationMessage;
  index: number;
  toggleTooltip: (index: number) => void;
  tooltipIndex: number;
}> = ({ productPromotion, index, toggleTooltip, tooltipIndex }) => {
  const viewRef = useDoActionWhenVisible(() =>
    trackPromotionalEventsPdpPromoBannerView(
      productPromotion.type ?? IInformationMessage.Type.TYPE_UNKNOWN
    )
  );
  return (
    <ProductPromotionContainer
      ref={viewRef}
      onClick={() => toggleTooltip(index)}
      $bgColor={productPromotion.background_color}
      data-test-id={`productPromoBanner:${productPromotion.type}`}
    >
      <Spacer size={10} />
      <PromoIcon {...productPromotion} />
      <PromotionTypography $textColor={productPromotion.text_color}>
        {productPromotion.title ? (
          <Title color={productPromotion.title_color}>
            {productPromotion.title}
          </Title>
        ) : null}
        {productPromotion.message_prefix ? (
          <MessagePrefix color={productPromotion.prefix_color}>
            {productPromotion.message_prefix}
          </MessagePrefix>
        ) : null}
        <FormattedText text={productPromotion.text} />
        {productPromotion.tooltip ? (
          <>
            &nbsp;
            <Tooltip
              isOpen={tooltipIndex === index}
              innerClassName="tooltip-inner"
              pointerOffset={130}
            >
              {productPromotion.tooltip}
              {productPromotion.help_link ? (
                <EmbeddedLink
                  target="_blank"
                  linkTo={productPromotion.help_link}
                  color={Core.text.inverse}
                  onClick={() => {
                    trackPromotionalEventsPdpPromoBannerClick(
                      productPromotion.type ??
                        IInformationMessage.Type.TYPE_UNKNOWN
                    );

                    toggleTooltip(index);
                  }}
                >
                  <StrictLocalMsg defaultMessage="Learn More" />
                </EmbeddedLink>
              ) : null}
            </Tooltip>
          </>
        ) : (
          <>
            &nbsp;
            <CtaLink {...productPromotion} />
          </>
        )}
      </PromotionTypography>
    </ProductPromotionContainer>
  );
};

interface ColoredTextProps {
  children: string;
  color?: string;
}

const HighlightedText = styled(Span).attrs({
  variant: "paragraphSansMedium",
})<{
  $customColor?: string;
  $withSpacing?: boolean;
}>`
  ${({ $customColor }) =>
    $customColor
      ? css`
          color: ${css`#${$customColor} !important`};
        `
      : ""};

  ${({ $withSpacing }) =>
    $withSpacing
      ? `
        margin-bottom: 4px;
        display: block;
      `
      : ""};
`;

const Title: React.FC<ColoredTextProps> = ({ children, color }) => (
  <HighlightedText $withSpacing $customColor={color}>
    {children}
  </HighlightedText>
);

const MessagePrefix: React.FC<ColoredTextProps> = ({ children, color }) => (
  <>
    <HighlightedText $customColor={color}>{children}</HighlightedText>{" "}
  </>
);

const ProductPromotionContainer = styled(Row).attrs({
  align: "flex-start",
})<{
  $bgColor?: string;
}>`
  width: 100%;
  border-radius: 4px;

  ${(props) =>
    props.$bgColor
      ? css`
          background: #${props.$bgColor};
          padding: ${getSpacing("2x")};
        `
      : ""};
`;

const PromotionTypography = styled(Typography)<{
  $textColor?: string;
}>`
  &&& {
    color: ${({ $textColor }) =>
      $textColor ? css`#${$textColor}` : Core.text.default};
  }
`;
